import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Bell, Download, MapPin, MessageCircle, Play } from 'lucide-react';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { BrandLoader } from '../brand/Brand';
import { feedPath } from '../listings/countries';
import apiService from '../../api';

const FEATURES = [
  [Play, 'Les vidéos des logements, en mode vidéo'],
  [MessageCircle, "L'agent sur WhatsApp ou par téléphone, en un geste"],
  [Bell, 'Une notification dès qu\'une nouvelle annonce correspond à votre recherche'],
  [MapPin, 'Les annonces de votre ville'],
];

// recouvro.com/app: the Android app, downloaded from here while it isn't on Google Play for
// everyone yet (and from Play once it is: AppRelease.play_url in the admin).
function AppDownloadPage() {
  const [release, setRelease] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    apiService.get('/api/v1/listings/app/android/')
      .then((r) => { setRelease(r); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>Application Recouvro Immobilier pour Android</title>
        <meta name="description" content="Les annonces immobilières au même endroit, sur votre téléphone : vidéos, contact WhatsApp, alertes. Téléchargez l'application Recouvro Immobilier." />
      </Helmet>
      <Header />
      <main className="flex-grow px-4 py-10 sm:py-14">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
          <img src="/logo192.png" alt="" width="88" height="88" className="mx-auto rounded-2xl" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Recouvro Immobilier</h1>
          <p className="mt-1 text-gray-600">Les annonces immobilières, au même endroit, sur votre téléphone.</p>

          <ul className="mt-6 space-y-3 text-left">
            {FEATURES.map(([Icon, text]) => (
              <li key={text} className="flex items-start gap-3 text-sm text-gray-700">
                <Icon size={18} className="shrink-0 mt-0.5 text-blue-600" /> {text}
              </li>
            ))}
          </ul>

          {status === 'loading' && <div className="flex justify-center mt-8"><BrandLoader /></div>}
          {status === 'error' && <p className="mt-8 text-sm text-red-600">L'application n'est pas encore disponible. Revenez bientôt.</p>}
          {status === 'ok' && (
            <>
              <a
                href={release.play_url || release.apk_url}
                className="mt-8 flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl py-3.5"
              >
                <Download size={18} /> {release.play_url ? 'Installer depuis Google Play' : "Télécharger l'application"}
              </a>
              <p className="mt-2 text-xs text-gray-500">Android · version {release.version_name}</p>

              {!release.play_url && (
                <div className="mt-6 text-left bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700">
                  <p className="font-semibold text-gray-900 mb-2">Pour l'installer</p>
                  <ol className="list-decimal pl-5 space-y-1">
                    <li>Touchez « Télécharger l'application » et ouvrez le fichier téléchargé.</li>
                    <li>Si le téléphone le demande, autorisez l'installation depuis votre navigateur (« sources inconnues »).</li>
                    <li>Touchez « Installer ». L'application vous préviendra des nouvelles versions.</li>
                  </ol>
                  <p className="mt-3 text-xs text-gray-500">L'application arrive bientôt sur Google Play.</p>
                </div>
              )}
            </>
          )}

          <Link to={feedPath()} className="mt-6 inline-block text-sm text-blue-700 underline">Voir les annonces sur le site</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default AppDownloadPage;
